// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Component, computed, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbTypeaheadModule, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of, merge, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';

import { BaseModalComponent, BaseModalConfig } from '@lfx/shared/components/base-modal';
import { AvatarComponent } from '@lfx/shared/components/avatar/avatar.component';
import { KeyProjectsService } from '@lfx/features/key-projects/services/key-projects.service';
import { HealthScore } from '@lfx/key-projects/key-projects.model';
import { DatadogErrorTrackingService } from '@lfx/core/services';

export interface Project {
  id: string;
  name: string;
  logo?: string;
  slug?: string;
  description?: string;
  healthScore?: HealthScore;
}

@Component({
  selector: 'lfx-add-projects-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbTypeaheadModule,
    BaseModalComponent,
    AvatarComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <lfx-base-modal
      [config]="modalConfig()"
      [isLoading]="isLoading()"
      [errorMessage]="errorMessage()"
      (confirmed)="addProjects()"
      (cancelled)="handleCancel()">

      <div class="tw-flex tw-items-start tw-gap-3 tw-p-3 tw-mb-4 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg">
        <i class="fas fa-info-circle tw-text-gray-400 tw-mt-1"></i>
        <span class="tw-text-sm tw-text-gray-600">This will be shared across all users in your company.</span>
      </div>
      
      <div class="add-projects-content">
        <!-- Typeahead Input with Caret -->
        <div class="typeahead-container">
          <input
            type="text"
            id="project-search-input"
            name="projectSearch"
            class="form-control"
            [(ngModel)]="searchTerm"
            [ngbTypeahead]="searchProjects"
            [resultTemplate]="projectResultTemplate"
            [inputFormatter]="formatProjectInput"
            [editable]="false"
            [focusFirst]="false"
            [showHint]="false"
            placeholder="Search and select projects"
            (selectItem)="addSelectedProject($event)"
            (focus)="handleInputFocus()"
            (click)="handleInputFocus()"
            #typeaheadInput="ngbTypeahead"
          />
          @if (isSearchingProjects()) {
            <i class="fal fa-spinner-third fa-spin typeahead-icon"></i>
          } @else {
            <i class="fal fa-angle-down typeahead-caret"
               [class.open]="typeaheadInput.isPopupOpen()"></i>
          }
        </div>

        <!-- Selected Projects Pills -->
        @if (selectedProjects().length > 0) {
          <div class="selected-projects">
            @for (project of selectedProjects(); track project.id) {
              <div class="project-pill">
                <span class="project-name">{{ project.name }}</span>
                <button type="button"
                        class="remove-btn"
                        (click)="removeSelectedProject(project)"
                        [attr.aria-label]="'Remove ' + project.name">
                  <i class="fal fa-times"></i>
                </button>
              </div>
            }
          </div>
        }
      </div>

      <!-- Project Result Template -->
      <ng-template #projectResultTemplate let-result="result" let-term="term">
        <div class="project-result">
          <lfx-avatar
            [imageURL]="result.logo"
            [name]="result.name"
            type="project"
            size="sm"
            [isObjectCover]="false">
          </lfx-avatar>
          <span class="project-name">{{ result.name }}</span>
        </div>
      </ng-template>
    </lfx-base-modal>
  `,
  styleUrl: './add-projects-modal.component.scss'
})
export class AddProjectsModalComponent {
  // Angular dependencies
  private readonly keyProjectsService = inject(KeyProjectsService);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly errorTracking = inject(DatadogErrorTrackingService);

  // Reactive state
  readonly selectedProjects = signal<Project[]>([]);
  readonly isLoading = signal<boolean>(false);
  readonly isSearchingProjects = signal<boolean>(false);
  readonly errorMessage = signal<string>('');

  // Component state
  searchTerm = '';
  private readonly focusSubject = new Subject<string>();

  // Computed properties for template
  protected readonly modalConfig = computed<BaseModalConfig>(() => ({
    title: 'Add project(s)',
    allowBackdropClose: false,
    confirmLabel: this.selectedProjects().length > 1 ? 'Add projects' : 'Add project',
    showCancel: true,
    showConfirm: true,
  }));

  // Template methods
  protected readonly searchProjects = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const focusText$ = this.focusSubject.asObservable();

    return merge(debouncedText$, focusText$).pipe(
      switchMap(term => this.performProjectSearch(term))
    );
  };

  protected handleInputFocus(): void {
    this.focusSubject.next(this.searchTerm);
  }

  protected readonly formatProjectInput = (project: Project): string => {
    return '';
  };

  protected addSelectedProject(event: any): void {
    event.preventDefault();
    const project = event.item as Project;

    if (project && !this.isProjectAlreadySelected(project)) {
      this.selectedProjects.update(projects => [...projects, project]);
      this.clearSearchTerm();
    }
  }

  protected removeSelectedProject(project: Project): void {
    this.selectedProjects.update(projects =>
      projects.filter(p => p.id !== project.id)
    );
  }

  protected addProjects(): void {
    const projects = this.selectedProjects();
    if (projects.length === 0) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');

    // Convert projects to projectSlugs format (projectId:slug)
    const projectSlugs = projects.map(p => `${p.id}:${p.slug || p.id}`);

    this.keyProjectsService.addProjectsToWorkspace(projectSlugs).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.activeModal.close({
          success: true,
          projects: projects,
          projectCount: projects.length,
          message: projects.length === 1
            ? `${projects[0].name} has been added successfully`
            : `${projects.length} projects have been added successfully`
        });
      },
      error: (error) => {
        this.isLoading.set(false);
        this.errorMessage.set('Failed to add projects. Please try again.');
        console.error('Add projects error:', error);
      }
    });
  }

  protected handleCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  // Private helper methods
  private performProjectSearch(term: string): Observable<Project[]> {
    // For focus events or very short terms, show popular/recent projects
    const searchTerm = term?.length < 2 ? '' : term;

    this.isSearchingProjects.set(true);
    return this.keyProjectsService.searchProjects(searchTerm).pipe(
      switchMap(results => {
        const filteredResults = this.filterOutSelectedProjects(results);
        this.isSearchingProjects.set(false);
        return of(filteredResults);
      }),
      catchError(error => this.handleSearchError(error))
    );
  }

  private filterOutSelectedProjects(results: Project[]): Project[] {
    const selectedIds = this.selectedProjects().map(p => p.id);
    return results.filter(p => !selectedIds.includes(p.id));
  }

  private handleSearchError(error: unknown): Observable<Project[]> {
    this.isSearchingProjects.set(false);
    this.errorMessage.set('Failed to search projects. Please try again.');

    // Track search error with context
    this.errorTracking.trackCustomError('Project search failed', {
      feature: 'key-projects',
      action: 'search-projects',
      component: 'AddProjectsModalComponent',
      searchTerm: this.searchTerm,
    });

    console.error('Project search error:', error);
    return of([]);
  }

  private isProjectAlreadySelected(project: Project): boolean {
    return this.selectedProjects().some(p => p.id === project.id);
  }

  private clearSearchTerm(): void {
    this.searchTerm = '';
  }
}
